import React, { FormEvent, KeyboardEvent, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import styles from './AdminUsers.module.scss';
import { AdminRoleDto, AdminUserDto, PagedResult } from '../../../types/admin.types';
import {
    AdminUserListFilters,
    fetchAdminRoles,
    fetchAdminUsersPaged
} from '../../../services/admin.service';
import { useUserState } from '../../../providers/user-state.provider.ts';
import { Routes } from '../../../types/constants';

const pageSize = 25;
const emptyList: PagedResult<AdminUserDto> = {
    Results: [],
    PageIndex: 1,
    PageSize: pageSize,
    TotalPages: 0,
    TotalCount: 0
};

const accountStatusValues = new Set(['enabled', 'disabled']);
const passwordStatusValues = new Set(['ready', 'reset']);
const subscriptionStatusValues = new Set(['active', 'canceling', 'canceled', 'none']);

const formatDate = (value?: string) => {
    if (!value) {
        return '-';
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString();
};

const getSubscriptionState = (user: AdminUserDto) => {
    const status = user.planStatus?.toLowerCase();
    if (!status) {
        return { label: 'None', className: styles.statusNeutral };
    }
    if (status === 'canceled' || status === 'incomplete_expired') {
        return { label: 'Canceled', className: styles.statusNeutral };
    }
    if (user.cancelAtPeriodEnd) {
        return { label: 'Canceling', className: styles.statusWarning };
    }
    if (status === 'active' || status === 'trialing') {
        return { label: status === 'trialing' ? 'Trialing' : 'Active', className: styles.statusSuccess };
    }

    return { label: status.replace(/_/g, ' '), className: styles.statusWarning };
};

const AdminUsers = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { getUserInfo } = useUserState();
    const token = getUserInfo()?.token;
    const [listData, setListData] = useState<PagedResult<AdminUserDto>>(emptyList);
    const [roles, setRoles] = useState<AdminRoleDto[]>([]);
    const [searchInput, setSearchInput] = useState(searchParams.get('q') ?? '');
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [roleErrorMessage, setRoleErrorMessage] = useState<string | null>(null);
    const [requestVersion, setRequestVersion] = useState(0);

    const pageIndex = Math.max(1, Number(searchParams.get('page')) || 1);
    const filters = useMemo<AdminUserListFilters>(() => {
        const accountStatus = searchParams.get('account') ?? '';
        const passwordStatus = searchParams.get('password') ?? '';
        const subscriptionStatus = searchParams.get('subscription') ?? '';
        return {
            query: searchParams.get('q') ?? '',
            role: searchParams.get('role') ?? '',
            accountStatus: accountStatusValues.has(accountStatus)
                ? accountStatus as AdminUserListFilters['accountStatus']
                : '',
            passwordStatus: passwordStatusValues.has(passwordStatus)
                ? passwordStatus as AdminUserListFilters['passwordStatus']
                : '',
            subscriptionStatus: subscriptionStatusValues.has(subscriptionStatus)
                ? subscriptionStatus as AdminUserListFilters['subscriptionStatus']
                : ''
        };
    }, [searchParams]);

    useEffect(() => {
        setSearchInput(filters.query);
    }, [filters.query]);

    useEffect(() => {
        let active = true;
        fetchAdminRoles(token)
            .then((response) => {
                if (active) {
                    setRoleErrorMessage(null);
                    setRoles(response
                        .map((role: any) => ({ id: role.id ?? role.Id ?? '', name: role.name ?? role.Name ?? '' }))
                        .filter((role) => role.name.trim().length > 0)
                        .sort((left, right) => left.name.localeCompare(right.name)));
                }
            })
            .catch(() => {
                if (active) {
                    setRoleErrorMessage('Role filters are temporarily unavailable. The user directory remains usable.');
                }
            });
        return () => {
            active = false;
        };
    }, [token]);

    useEffect(() => {
        let active = true;
        setIsLoading(true);
        setErrorMessage(null);
        fetchAdminUsersPaged(pageIndex, pageSize, filters, token)
            .then((response) => {
                if (!active) {
                    return;
                }
                setListData(response);
                if (response.TotalPages > 0 && pageIndex > response.TotalPages) {
                    const next = new URLSearchParams(searchParams);
                    next.set('page', String(response.TotalPages));
                    setSearchParams(next, { replace: true });
                }
            })
            .catch(() => {
                if (active) {
                    setErrorMessage('Unable to load users.');
                    setListData(emptyList);
                }
            })
            .finally(() => {
                if (active) {
                    setIsLoading(false);
                }
            });
        return () => {
            active = false;
        };
    }, [filters, pageIndex, requestVersion, searchParams, setSearchParams, token]);

    const updateParams = (changes: Record<string, string | null>) => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => {
            if (value) {
                next.set(key, value);
            } else {
                next.delete(key);
            }
        });
        if (!Object.prototype.hasOwnProperty.call(changes, 'page')) {
            next.delete('page');
        }
        setSearchParams(next);
    };

    const handleSearch = (event: FormEvent) => {
        event.preventDefault();
        updateParams({ q: searchInput.trim() || null });
    };

    const returnTo = `${location.pathname}${location.search}`;
    const openUser = (userId?: string) => {
        if (!userId) {
            setErrorMessage('This account is missing its stable identifier. Reload and try again.');
            return;
        }
        navigate(`${Routes.ADMIN_USERS}/${encodeURIComponent(userId)}`, { state: { usersReturnTo: returnTo } });
    };
    const openWatchHistory = (userId: string) => {
        navigate(`${Routes.ADMIN_USERS}/${encodeURIComponent(userId)}/watch-history`, {
            state: { usersReturnTo: returnTo }
        });
    };

    const handleRowKeyDown = (event: KeyboardEvent<HTMLTableRowElement>, userId?: string) => {
        if ((event.key === 'Enter' || event.key === ' ') && event.target === event.currentTarget) {
            event.preventDefault();
            openUser(userId);
        }
    };

    const activeFilters = [
        filters.query ? { key: 'q', label: `Search: ${filters.query}` } : null,
        filters.role ? { key: 'role', label: `Role: ${filters.role === 'none' ? 'None' : filters.role}` } : null,
        filters.accountStatus ? { key: 'account', label: `Account: ${filters.accountStatus}` } : null,
        filters.passwordStatus ? { key: 'password', label: `Password: ${filters.passwordStatus === 'reset' ? 'Reset required' : 'Ready'}` } : null,
        filters.subscriptionStatus ? { key: 'subscription', label: `Subscription: ${filters.subscriptionStatus}` } : null
    ].filter((filter): filter is { key: string; label: string } => filter !== null);

    return (
        <div className={styles.page}>
            <header className={styles.pageHeader}>
                <div>
                    <div className={styles.eyebrow}>Account administration</div>
                    <h1>Users</h1>
                    <p>Find accounts quickly and manage identity, access, subscriptions, and support actions.</p>
                </div>
                <button
                    className={styles.primaryButton}
                    type="button"
                    onClick={() => navigate(Routes.ADMIN_USER_CREATE, { state: { usersReturnTo: returnTo } })}
                >
                    Create user
                </button>
            </header>

            <section className={styles.filterPanel} aria-label="User filters">
                <form className={styles.searchRow} onSubmit={handleSearch}>
                    <label className={styles.searchField}>
                        <span>Search users</span>
                        <input
                            value={searchInput}
                            onChange={(event) => setSearchInput(event.target.value)}
                            placeholder="Username, name, or email"
                            maxLength={200}
                        />
                    </label>
                    <button className={styles.secondaryButton} type="submit">Search</button>
                </form>
                <div className={styles.filterGrid}>
                    <label>
                        <span>Role</span>
                        <select value={filters.role} onChange={(event) => updateParams({ role: event.target.value || null })}>
                            <option value="">All roles</option>
                            <option value="none">No role</option>
                            {roles.map((role) => <option key={role.id || role.name} value={role.name}>{role.name}</option>)}
                        </select>
                    </label>
                    <label>
                        <span>Account</span>
                        <select value={filters.accountStatus} onChange={(event) => updateParams({ account: event.target.value || null })}>
                            <option value="">All accounts</option>
                            <option value="enabled">Enabled</option>
                            <option value="disabled">Disabled</option>
                        </select>
                    </label>
                    <label>
                        <span>Password</span>
                        <select value={filters.passwordStatus} onChange={(event) => updateParams({ password: event.target.value || null })}>
                            <option value="">All password states</option>
                            <option value="ready">Ready</option>
                            <option value="reset">Reset required</option>
                        </select>
                    </label>
                    <label>
                        <span>Subscription</span>
                        <select value={filters.subscriptionStatus} onChange={(event) => updateParams({ subscription: event.target.value || null })}>
                            <option value="">All subscriptions</option>
                            <option value="active">Active</option>
                            <option value="canceling">Canceling</option>
                            <option value="canceled">Canceled</option>
                            <option value="none">None</option>
                        </select>
                    </label>
                </div>
                {roleErrorMessage ? <div className={styles.inlineInfo} role="status">{roleErrorMessage}</div> : null}
                {activeFilters.length > 0 ? (
                    <div className={styles.activeFilters} aria-label="Active filters">
                        {activeFilters.map((filter) => (
                            <button key={filter.key} type="button" onClick={() => updateParams({ [filter.key]: null })}>
                                {filter.label}<span aria-hidden="true">×</span>
                            </button>
                        ))}
                        <button className={styles.clearFilters} type="button" onClick={() => setSearchParams({})}>Clear all</button>
                    </div>
                ) : null}
            </section>

            <section className={styles.listPanel} aria-labelledby="users-results-heading">
                <div className={styles.sectionHeader}>
                    <div>
                        <h2 id="users-results-heading">User directory</h2>
                        <p>{isLoading ? 'Loading results…' : `${listData.TotalCount.toLocaleString()} ${listData.TotalCount === 1 ? 'user' : 'users'}`}</p>
                    </div>
                    {errorMessage ? <button className={styles.textButton} type="button" onClick={() => setRequestVersion((value) => value + 1)}>Retry</button> : null}
                </div>

                {errorMessage ? <div className={styles.errorBanner} role="alert">{errorMessage}</div> : null}
                {isLoading ? <div className={styles.loadingState}>Loading users…</div> : null}
                {!isLoading && !errorMessage && listData.Results.length === 0 ? (
                    <div className={styles.emptyState}>
                        <h3>No users found</h3>
                        <p>Try removing a filter or searching with a different name or email.</p>
                    </div>
                ) : null}

                {!isLoading && listData.Results.length > 0 ? (
                    <>
                        <div className={styles.tableWrap}>
                            <table className={styles.userTable}>
                                <thead>
                                    <tr>
                                        <th>User</th>
                                        <th>Access</th>
                                        <th>Account</th>
                                        <th>Password</th>
                                        <th>Subscription</th>
                                        <th>Created</th>
                                        <th><span className={styles.visuallyHidden}>Actions</span></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {listData.Results.map((user) => {
                                        const subscription = getSubscriptionState(user);
                                        const name = `${user.firstName} ${user.lastName}`.trim() || user.username;
                                        return (
                                            <tr
                                                key={user.userId ?? user.username}
                                                tabIndex={0}
                                                onClick={(event) => {
                                                    if (!(event.target as HTMLElement).closest('a, button, summary, details')) {
                                                        openUser(user.userId);
                                                    }
                                                }}
                                                onKeyDown={(event) => handleRowKeyDown(event, user.userId)}
                                                aria-label={`Manage ${user.username}`}
                                            >
                                                <td>
                                                    <button className={styles.userIdentity} type="button" onClick={() => openUser(user.userId)}>
                                                        <span className={styles.avatar} aria-hidden="true">{name.slice(0, 1).toUpperCase()}</span>
                                                        <span>
                                                            <strong>{name}</strong>
                                                            <small>{user.username} · {user.email}</small>
                                                        </span>
                                                    </button>
                                                </td>
                                                <td><div className={styles.roleList}>{user.roles.length > 0 ? user.roles.map((role) => <span key={role}>{role}</span>) : <em>None</em>}</div></td>
                                                <td><span className={`${styles.statusBadge} ${user.enabled ? styles.statusSuccess : styles.statusDanger}`}>{user.enabled ? 'Enabled' : 'Disabled'}</span></td>
                                                <td><span className={`${styles.statusBadge} ${user.requiresPasswordReset ? styles.statusWarning : styles.statusNeutral}`}>{user.requiresPasswordReset ? 'Reset required' : 'Ready'}</span></td>
                                                <td><span className={`${styles.statusBadge} ${subscription.className}`}>{subscription.label}</span></td>
                                                <td>{formatDate(user.createdDate)}</td>
                                                <td>
                                                    <details className={styles.rowMenu}>
                                                        <summary aria-label={`Actions for ${user.username}`}>•••</summary>
                                                        <div>
                                                            <button type="button" onClick={() => openUser(user.userId)}>Manage user</button>
                                                            <button type="button" onClick={() => openWatchHistory(user.userId)}>Watch history</button>
                                                        </div>
                                                    </details>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <div className={styles.mobileCards}>
                            {listData.Results.map((user) => {
                                const subscription = getSubscriptionState(user);
                                return (
                                    <button key={user.userId ?? user.username} className={styles.mobileUserCard} type="button" onClick={() => openUser(user.userId)}>
                                        <span className={styles.mobileCardTop}><strong>{`${user.firstName} ${user.lastName}`.trim() || user.username}</strong><small>{formatDate(user.createdDate)}</small></span>
                                        <span>{user.username}</span>
                                        <span className={styles.mobileEmail}>{user.email}</span>
                                        <span className={styles.mobileBadges}>
                                            <span className={`${styles.statusBadge} ${user.enabled ? styles.statusSuccess : styles.statusDanger}`}>{user.enabled ? 'Enabled' : 'Disabled'}</span>
                                            <span className={`${styles.statusBadge} ${subscription.className}`}>{subscription.label}</span>
                                            {user.requiresPasswordReset ? <span className={`${styles.statusBadge} ${styles.statusWarning}`}>Reset required</span> : null}
                                        </span>
                                        <span className={styles.roleList}>{user.roles.map((role) => <span key={role}>{role}</span>)}</span>
                                    </button>
                                );
                            })}
                        </div>

                        <nav className={styles.pagination} aria-label="User list pages">
                            <button
                                type="button"
                                disabled={listData.PageIndex <= 1}
                                onClick={() => updateParams({ page: String(Math.max(1, listData.PageIndex - 1)) })}
                            >
                                Previous
                            </button>
                            <span>Page {listData.PageIndex} of {Math.max(1, listData.TotalPages)}</span>
                            <button
                                type="button"
                                disabled={listData.PageIndex >= listData.TotalPages}
                                onClick={() => updateParams({ page: String(listData.PageIndex + 1) })}
                            >
                                Next
                            </button>
                        </nav>
                    </>
                ) : null}
            </section>
        </div>
    );
};

export default AdminUsers;
